"use client";

import posthog from "posthog-js";

/**
 * "Learn More" link in the profile's NAR section header → `/nar-designations`
 * (TRU-58).
 *
 * A client leaf purely so the click can be captured: the success metric for
 * TRU-58 is the click-through rate from this link to the designations page, and
 * PostHog's autocapture cannot distinguish it from any other anchor.
 *
 * The href is RELATIVE on purpose — every public AD surface is host-scoped by
 * middleware, so a relative link keeps the visitor on the same org host without
 * this component needing to know which host that is.
 *
 * Kill switch mirrors `PostHogIdentify`: key presence only. With no key the SDK
 * was never initialized (`instrumentation-client.ts` gates init on the same
 * key), so the guard is defensive — and Turbopack dead-code-strips the capture
 * when the key is unset at build. Navigation must never depend on telemetry, so
 * the capture is also wrapped: a throwing SDK cannot swallow the click.
 */
export function NarLearnMoreLink({ agentId }: { agentId?: string }) {
  function handleClick() {
    if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;
    try {
      posthog.capture("nar_learn_more_clicked", {
        surface: "agent_profile",
        agent_id: agentId,
      });
    } catch {
      // Telemetry is never allowed to break the navigation.
    }
  }

  return (
    <a
      href="/nar-designations"
      onClick={handleClick}
      className="ml-auto whitespace-nowrap text-[13px] font-medium no-underline hover:underline focus-visible:underline focus-visible:outline-none"
      style={{ color: "var(--brand-primary)" }}
    >
      Learn More
    </a>
  );
}
